import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDataEntryContext } from "../context/reducer.provider";
import { useTimerContext } from "../context/timer.provider";
import {
  Addition,
  CarbonInjection,
  Charge,
  Heating,
  Probe,
  Process,
  ProcessResponse,
} from "../models/eaf.models";
import { Pourback } from "../models/ms.models";
import {
  UpsertAddition,
  UpsertHeating,
  UpsertPourbackCharge,
  deleteAdditionById,
  deleteChargeById,
  deleteHeatingById,
  deletePourbackChargeById,
  deleteProbeById,
  getEAFProcess,
  upsertCharge,
  upsertHeat,
  upsertInjection,
  upsertProbe,
  upsertProcess,
  upsertProcessData,
  upsertProcessEnd,
} from "../repositories/eaf.repositories";

export const useEAFDataEntryActions = () => {
  const navigate = useNavigate();

  const {
    state,
    actions: {
      setDEProcess,
      setErrorMessage,
      setIsDataLoading,
      setIsSubmitLoading,
      setNotificationMessage,
      setLastUpdate,
    },
  } = useDataEntryContext();

  const { restart } = useTimerContext();

  const loadData = async (processId: number | null) => {
    setIsDataLoading(true);
    const resp = await getEAFProcess({ ProcessId: processId });
    if (resp.ok) {
      const data = resp.data as ProcessResponse;
      const process = data.tables["0"].rows[0] as Process;
      setDEProcess({
        process,
        probes: data.tables["1"].rows,
        charges: data.tables["2"].rows,
        additions: data.tables["3"].rows,
        phases: data.tables["4"].rows,
        pourbacks: data.tables["5"].rows,
        slags: data.tables["6"].rows,
        carbonInjections: data.tables["7"].rows,
        heatings: data.tables["8"].rows,
        missingFields: data.tables["9"]?.rows ?? [],
      });
      setLastUpdate();
      navigate(`/eaf-data-entry/${process?.ProcessId}`);
    } else {
      setErrorMessage(resp.message!);
    }
    setIsDataLoading(false);
  };

  const refreshData = useCallback(async () => {
    if (state.deProcess?.process?.ProcessId) {
      loadData(state.deProcess.process.ProcessId).then(() => {
        restart();
      });
    } else {
      loadData(null).then(() => {
        restart();
      });
    }
  }, [state.deProcess?.process?.ProcessId]);

  const saveProcess = async (process: Process) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertProcess({
      ProcessId: process.ProcessId!,
      Start: process.Start!.toISOString(),
      End: process.End?.toISOString() || null,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Process saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveProcessEnd = async (process: Process) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertProcessEnd({
      ProcessId: process.ProcessId!,
      End: process.End?.toISOString() || null,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Process saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveProcessData = async ({
    processId,
    field,
    value,
  }: {
    processId: number;
    field: string;
    value: boolean | string | number | Date | null;
  }) => {
    setIsSubmitLoading(true);
    setErrorMessage("");

    const processedValue = value instanceof Date ? value.toISOString() : value;

    let extraField = null;
    let extraValue = null;
    if (field === "StartTap") {
      extraField = "StartHeat";
      extraValue = state.deProcess?.process?.StartHeat
        ? state.deProcess?.process?.StartHeat.toISOString()
        : null;
    } else if (field === "StartHeat") {
      extraField = "StartTap";
      extraValue = state.deProcess?.process?.StartTap
        ? state.deProcess?.process?.StartTap.toISOString()
        : null;
    }

    let resp;
    if (extraField) {
      resp = await upsertProcessData({
        processId,
        field,
        value: processedValue,
        extrafield: extraField,
        extravalue: extraValue,
      });
    } else {
      resp = await upsertProcessData({
        processId,
        field,
        value: processedValue,
      });
    }
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Process saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveProbe = async (probe: Probe) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertProbe({
      ProcessId: state.deProcess?.process!.ProcessId!,
      ProbeId: probe.ProbeId!,
      IsProbeManual: true,
      Timestamp: probe.Timestamp!.toISOString(),
      Temperature: probe.Temperature,
      Carbon: probe.Carbon,
      MWH: probe.MWH,
      Oxygen: probe.Oxygen,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Process saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveHeat = async ({
    heatId,
    field,
    value,
  }: {
    heatId: number;
    field: string;
    value: any;
  }) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertHeat({
      HeatId: heatId,
      field: field,
      value: value,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Process saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveCharge = async (charge: Charge) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertCharge({
      ProcessId: state.deProcess?.process!.ProcessId!,
      ChargeId: charge.ChargeId!,
      Timestamp: charge.Timestamp!.toISOString(),
      BucketId: charge.BucketId,
      Weight: charge.Weight!,
      Lime: charge.Lime!,
      IsNotFit: charge.IsNotFit,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Charge saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveChargeNotFit = async (charge: Charge, isNotFit: boolean) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertCharge({
      ProcessId: state.deProcess?.process!.ProcessId!,
      ChargeId: charge.ChargeId!,
      Timestamp: charge.Timestamp!.toISOString(),
      BucketId: charge.BucketId,
      Weight: charge.Weight!,
      Lime: charge.Lime!,
      IsChargeManual: charge.IsChargeManual,
      IsNotFit: isNotFit,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Charge saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveAddition = async (addition: Addition) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await UpsertAddition({
      ProcessId: state.deProcess?.process!.ProcessId!,
      AdditionId: addition?.AdditionId,
      Timestamp: addition?.Timestamp!.toISOString(),
      Weight: addition?.Weight!,
      MaterialId: addition?.MaterialId,
      Source: addition?.Source,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Addition saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveHeating = async (heating: Heating) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await UpsertHeating({
      ProcessId: state.deProcess?.process!.ProcessId!,
      HeatingId: heating?.HeatingId,
      StartTime: heating?.StartTime!.toISOString(),
      EndTime: heating?.EndTime!.toISOString(),
      Energy: heating?.Energy!,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Heating saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const savePourbackCharge = async (pourback: Pourback) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await UpsertPourbackCharge({
      PourbackId: pourback?.PourbackId,
      HeatId: pourback?.HeatId,
      DestinationProcessId: state.deProcess?.process!.ProcessId!,
      ChargedWeight: pourback?.PourbackWeight,
      ChargedTime: pourback?.ChargedTime?.toISOString()!,
      RemainingWeight:
        pourback?.ChargedWeight === 0 ? null : pourback?.ChargedWeight,
      Comments: pourback?.Comments,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Pourback saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveInjection = async (injection: CarbonInjection) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertInjection({
      ProcessId: state.deProcess?.process.ProcessId!,
      InjectionTypeId: injection.InjectionTypeId,
      Injector1: injection.Injector1,
      Injector2: injection.Injector2,
      Injector3: injection.Injector3,
      InjectorDoor: injection.InjectorDoor,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Injection saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const deleteProbe = async (probeId: number) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await deleteProbeById({
      ProbeId: probeId,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Probe deleted successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const deleteCharge = async (chargeId: number) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await deleteChargeById({
      ChargeId: chargeId,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Charge deleted successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const deleteAddition = async (additionId: number) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await deleteAdditionById({
      AdditionId: additionId,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Addition deleted successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const deleteHeating = async (heatingId: number) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await deleteHeatingById({
      HeatingId: heatingId,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Heating deleted successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const deletePourbackCharge = async (pourbackId: number) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await deletePourbackChargeById({
      PourbackId: pourbackId,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Pourback deleted successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  return {
    saveProcess,
    saveProcessEnd,
    loadData,
    refreshData,
    saveProcessData,
    saveProbe,
    saveHeat,
    saveCharge,
    saveChargeNotFit,
    saveAddition,
    saveHeating,
    savePourbackCharge,
    saveInjection,
    deleteProbe,
    deleteCharge,
    deleteAddition,
    deleteHeating,
    deletePourbackCharge,
  };
};
